import { BRAND, FONTS } from '../styles/tokens';
import { TRAININGS } from '../data/trainings';
import { TRAINING_INTEREST, CONSULTATION_INTEREST } from '../data/content';
import { pagePath } from '../data/pages';
import { PageHero } from './PageShell';
import { NotFound } from './AssessmentPage';
import { useIsMobile } from '../hooks/useIsMobile';
import { track } from '../services/analytics';
import type { LeadPrefill } from '../types';

interface Props { id: string; onLead: (p: LeadPrefill) => void }

const CRUMBS = [{ label: 'Home', href: '/' }, { label: 'Programs', href: '/#training' }];

export function ProgramPage({ id, onLead }: Props) {
  const program = TRAININGS.find(t => t.id === id);
  const m = useIsMobile();
  if (!program) return <NotFound crumbs={CRUMBS} what="program" />;
  const others = TRAININGS.filter(t => t.id !== id);
  const request = () => { track('speaking_cta_click', { talk: `program:${program.id}` }); onLead({ interest: TRAINING_INTEREST, message: `Training of interest: ${program.title}` }); };

  const primaryBtn = { background: BRAND.warm, color: BRAND.navy, border: 'none', padding: m ? '18px 28px' : '16px 36px', borderRadius: m ? 12 : 8, fontSize: m ? 16 : 15, fontWeight: 700, fontFamily: FONTS.body, cursor: 'pointer', width: m ? '100%' : 'auto' } as const;

  return (
    <>
      <PageHero eyebrow="Program for your organization" icon={program.icon} title={program.title} subtitle={program.short} crumbs={CRUMBS}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 20 }}>
          {program.audience.split('·').map(a => a.trim()).filter(Boolean).map(a => (
            <span key={a} style={{ fontFamily: FONTS.body, fontSize: 12, padding: '7px 14px', borderRadius: 24, background: 'rgba(124,58,165,0.2)', color: BRAND.warmLight, fontWeight: 600 }}>{a}</span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 18, flexWrap: 'wrap', fontFamily: FONTS.body, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
          <span>⏱ {program.duration}</span>
          <span>📍 {program.format}</span>
        </div>
      </PageHero>

      <section style={{ background: BRAND.white, padding: m ? '56px 20px' : '88px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: m ? '1fr' : '1.3fr 1fr', gap: m ? 32 : 56 }}>
          <div>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 26 : 32, fontWeight: 700, color: BRAND.navy, marginBottom: 18 }}>What's covered</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {program.details.map((d, i) => (
                <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: BRAND.warm, flexShrink: 0, marginTop: 9 }} />
                  <span style={{ fontFamily: FONTS.body, fontSize: m ? 15 : 16, lineHeight: 1.7, color: BRAND.text }}>{d}</span>
                </li>
              ))}
            </ul>
            <p style={{ fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.8, color: BRAND.textMid, marginTop: 24 }}>
              Every program is customized to your organization's roles, cases, and gaps. Tracy meets with you beforehand to understand your team's goals. Virtual sessions are interactive — breakout discussion, case studies, and Q&amp;A — not just slides.
            </p>
          </div>
          <aside style={{ background: BRAND.cream, border: `1px solid ${BRAND.border}`, borderRadius: 16, padding: m ? 24 : 32, alignSelf: 'start' }}>
            <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND.textLight, marginBottom: 10 }}>Bring this to your team</div>
            <p style={{ fontFamily: FONTS.body, fontSize: 15, lineHeight: 1.7, color: BRAND.textMid, marginBottom: 20 }}>Tell Tracy about your organization, audience size, and preferred format. She responds within 48 hours. Investment available upon request.</p>
            <button onClick={request} style={primaryBtn}>Request this program →</button>
            <button onClick={() => onLead({ interest: CONSULTATION_INTEREST, message: `Consultation related to: ${program.title}` })}
              style={{ marginTop: 10, background: 'transparent', color: BRAND.navy, border: `1.5px solid ${BRAND.border}`, padding: m ? '16px 24px' : '14px 28px', borderRadius: m ? 12 : 8, fontSize: 14, fontWeight: 600, fontFamily: FONTS.body, cursor: 'pointer', width: '100%' }}>
              Ask about a consultation
            </button>
          </aside>
        </div>
      </section>

      <section style={{ background: BRAND.navy, padding: m ? '48px 20px' : '72px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 24 : 30, fontWeight: 700, color: BRAND.white, marginBottom: 20 }}>Other programs</h2>
          <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
            {others.map(t => (
              <a key={t.id} href={pagePath('program', t.slug)} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18, textDecoration: 'none' }}>
                <span aria-hidden style={{ fontSize: 24 }}>{t.icon}</span>
                <span>
                  <span style={{ display: 'block', fontFamily: FONTS.heading, fontSize: 18, fontWeight: 700, color: BRAND.white }}>{t.title}</span>
                  <span style={{ display: 'block', fontFamily: FONTS.body, fontSize: 11, color: BRAND.warm, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{t.audience}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
